import React, { useState, useEffect, useCallback } from 'react';
import {
  Trigger,
  Workflow,
  TeamMemberInfo,
  EventDefinition,
  EventCategory,
  Tag,
} from '../types';
import {
  triggerService,
  eventDefinitionService,
  workflowService,
  teamService,
  tagService,
} from '../services';
import { CreateTriggerRequest } from '../services/triggerService';
import { CreateEventDefinitionRequest } from '../services/eventDefinitionService';
import PageTopBar from '../components/ui/PageTopBar';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import Card from '../components/ui/Card';
import EmptyState from '../components/ui/EmptyState';
import SearchBar from '../components/ui/SearchBar';
import Select from '../components/ui/Select';
import ConfirmationModal from '../components/common/ConfirmationModal';
import TriggerFormModal from './triggers/TriggerFormModal';
import EventsTab from './triggers/EventsTab';
import {
  getEventCategoryLabel,
  getAssetScopeLabel,
  getAssignmentStrategyLabel,
} from './triggers/triggerOptions';

const formatRelativeDate = (iso?: string): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return d.toLocaleDateString();
};

const categoryVariants: Record<EventCategory, 'primary' | 'secondary' | 'success' | 'warning'> = {
  schedule: 'primary',
  external: 'warning',
  internal: 'success',
  workflow: 'secondary',
};

type TabKey = 'triggers' | 'events';

const Triggers: React.FC = () => {
  // ── Active tab ──
  const [activeTab, setActiveTab] = useState<TabKey>('triggers');

  // ── Reference data shared by both tabs ──
  const [triggers, setTriggers] = useState<Trigger[]>([]);
  const [eventDefinitions, setEventDefinitions] = useState<EventDefinition[]>([]);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMemberInfo[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ── Triggers tab filters ──
  const [searchQuery, setSearchQuery] = useState('');
  const [eventDefFilter, setEventDefFilter] = useState<string>('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');

  // ── Triggers tab modals ──
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editing, setEditing] = useState<Trigger | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // ─── Loaders ──────────────────────────────────────────────────────────────

  const loadTriggers = useCallback(async () => {
    try {
      const res = await triggerService.list(1, 100, {
        search: searchQuery || undefined,
        eventDefinitionId: eventDefFilter || undefined,
        isActive:
          activeFilter === 'all' ? undefined : activeFilter === 'active' ? true : false,
      });
      setTriggers(res.data);
    } catch (err: any) {
      console.error('Failed to load triggers:', err);
      setError(err?.response?.data?.detail || 'Failed to load triggers');
    }
  }, [searchQuery, eventDefFilter, activeFilter]);

  const loadEventDefinitions = useCallback(async () => {
    try {
      const res = await eventDefinitionService.list(1, 200);
      setEventDefinitions(res.data);
    } catch (err: any) {
      console.error('Failed to load event definitions:', err);
      setError(err?.response?.data?.detail || 'Failed to load event definitions');
    }
  }, []);

  const loadTags = useCallback(async () => {
    try {
      const data = await tagService.list();
      setTags(data);
    } catch (err) {
      console.error('Failed to load tags:', err);
    }
  }, []);

  const loadReferences = useCallback(async () => {
    try {
      const [wf, tm] = await Promise.all([
        workflowService.getWorkflows(1, 100),
        teamService.getTeamMembers().catch(() => []),
      ]);
      setWorkflows(wf.data);
      setTeamMembers(tm);
    } catch (err) {
      console.error('Failed to load references:', err);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      loadTriggers(),
      loadEventDefinitions(),
      loadReferences(),
      loadTags(),
    ]).finally(() => setLoading(false));
  }, [loadTriggers, loadEventDefinitions, loadReferences, loadTags]);

  // ─── Mutations ────────────────────────────────────────────────────────────

  const handleSubmitTrigger = async (data: CreateTriggerRequest, id?: string) => {
    if (id) {
      await triggerService.update(id, data);
    } else {
      await triggerService.create(data);
    }
    await Promise.all([loadTriggers(), loadEventDefinitions()]);
  };

  const handleDeleteTrigger = async () => {
    if (!deletingId) return;
    try {
      await triggerService.delete(deletingId);
      setDeletingId(null);
      await Promise.all([loadTriggers(), loadEventDefinitions()]);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to delete trigger');
    }
  };

  const handleSubmitEventDefinition = async (
    data: CreateEventDefinitionRequest,
    id?: string,
  ) => {
    if (id) {
      await eventDefinitionService.update(id, data);
    } else {
      await eventDefinitionService.create(data);
    }
    await loadEventDefinitions();
  };

  const handleDeleteEventDefinition = async (id: string) => {
    try {
      await eventDefinitionService.delete(id);
      await loadEventDefinitions();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to delete event definition');
    }
  };

  const openCreate = () => {
    setEditing(null);
    setIsFormOpen(true);
  };

  const openEdit = (trigger: Trigger) => {
    setEditing(trigger);
    setIsFormOpen(true);
  };

  const hasActiveFilters = !!searchQuery || !!eventDefFilter || activeFilter !== 'all';
  const clearFilters = () => {
    setSearchQuery('');
    setEventDefFilter('');
    setActiveFilter('all');
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="h-full flex flex-col bg-neutral-50">
      <PageTopBar
        title="Triggers"
        description="Automatically kick off workflows when events happen"
      />

      <div className="flex-1 overflow-y-auto">
        <div className="w-full px-6 lg:px-8 py-8">
          {/* Tab bar */}
          <div className="mb-6 flex items-center gap-1 border-b border-neutral-200">
            {([
              { key: 'triggers', label: 'Triggers', count: triggers.length },
              { key: 'events', label: 'Events', count: eventDefinitions.length },
            ] as const).map(t => {
              const isActive = activeTab === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setActiveTab(t.key)}
                  className={`px-4 py-2.5 -mb-px border-b-2 text-sm font-medium transition-colors ${
                    isActive
                      ? 'border-primary-600 text-primary-700'
                      : 'border-transparent text-neutral-500 hover:text-neutral-900'
                  }`}
                >
                  {t.label}
                  <span className="ml-2 text-xs text-neutral-400">{t.count}</span>
                </button>
              );
            })}
          </div>

          {/* Error */}
          {error && (
            <div className="mb-4 p-3 bg-danger-50 border border-danger-200 rounded-lg text-sm text-danger-700 flex items-center justify-between">
              <span>{error}</span>
              <button
                onClick={() => setError(null)}
                className="text-danger-500 hover:text-danger-700 font-medium"
              >
                Dismiss
              </button>
            </div>
          )}

          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-600 border-t-transparent" />
            </div>
          ) : activeTab === 'triggers' ? (
            <>
              {/* Filter / action bar */}
              <div className="mb-6 flex flex-wrap items-center gap-3">
                <SearchBar
                  value={searchQuery}
                  onChange={setSearchQuery}
                  placeholder="Search triggers..."
                />
                <Select
                  value={eventDefFilter}
                  onChange={e => setEventDefFilter(e.target.value)}
                  options={eventDefinitions.map(ed => ({ value: ed.id, label: ed.name }))}
                  placeholder="All events"
                  className="w-56"
                />
                <div className="flex h-9 bg-surface border border-neutral-200 rounded-lg p-0.5 items-center">
                  {(['all', 'active', 'inactive'] as const).map(opt => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setActiveFilter(opt)}
                      className={`px-3 h-7 rounded-md text-sm font-medium transition-colors capitalize ${
                        activeFilter === opt
                          ? 'bg-primary-100 text-primary-700'
                          : 'text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50'
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
                {hasActiveFilters && (
                  <Button variant="outline" size="sm" onClick={clearFilters}>
                    Clear all
                  </Button>
                )}

                <div className="ml-auto">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={openCreate}
                    disabled={eventDefinitions.length === 0}
                    title={
                      eventDefinitions.length === 0
                        ? 'Define an event first on the Events tab'
                        : 'New trigger'
                    }
                  >
                    New Trigger
                  </Button>
                </div>
              </div>

              {/* Empty state — no event defs at all */}
              {eventDefinitions.length === 0 ? (
                <EmptyState
                  title="Start with an event"
                  description="Triggers fire on event definitions. Create your first event on the Events tab."
                  action={{
                    label: 'Go to Events',
                    onClick: () => setActiveTab('events'),
                  }}
                />
              ) : triggers.length === 0 ? (
                <EmptyState
                  title={hasActiveFilters ? 'No triggers match your filters' : 'No triggers yet'}
                  description={
                    hasActiveFilters
                      ? 'Try adjusting your search or filters.'
                      : 'Create a trigger to fire a workflow when an event happens.'
                  }
                  action={
                    hasActiveFilters
                      ? { label: 'Clear filters', onClick: clearFilters }
                      : { label: 'New Trigger', onClick: openCreate }
                  }
                />
              ) : (
                <Card variant="default" className="overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-neutral-50 border-b border-neutral-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                          Trigger
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                          Event
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                          Workflow
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                          Scope
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                          Assignment
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                          Last fired
                        </th>
                        <th className="px-4 py-3 w-32 text-right text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100">
                      {triggers.map(trigger => (
                        <tr
                          key={trigger.id}
                          className="hover:bg-neutral-50 transition-colors"
                        >
                          <td className="px-4 py-3">
                            <div className="font-medium text-neutral-900">{trigger.name}</div>
                            {trigger.description && (
                              <div className="text-xs text-neutral-500 mt-0.5 truncate max-w-xs">
                                {trigger.description}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-neutral-700">
                            <div className="flex items-center gap-2">
                              {trigger.eventCategory && (
                                <Badge
                                  variant={categoryVariants[trigger.eventCategory]}
                                  size="sm"
                                >
                                  {getEventCategoryLabel(trigger.eventCategory)}
                                </Badge>
                              )}
                              <span className="truncate">
                                {trigger.eventDefinitionName || (
                                  <span className="text-neutral-400 italic">missing</span>
                                )}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-neutral-700">
                            {trigger.workflowName || (
                              <span className="text-neutral-400 italic">missing</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-neutral-600">
                            {getAssetScopeLabel(trigger.assetScope)}
                          </td>
                          <td className="px-4 py-3 text-sm text-neutral-600">
                            {trigger.assetScope === 'coverage' ? (
                              <span className="text-neutral-500">Coverage owner</span>
                            ) : (
                              <>
                                <div>
                                  {trigger.assignmentStrategy
                                    ? getAssignmentStrategyLabel(trigger.assignmentStrategy)
                                    : '—'}
                                </div>
                                {trigger.assignmentStrategy === 'specific_user' && trigger.assigneeName && (
                                  <div className="text-xs text-neutral-500">{trigger.assigneeName}</div>
                                )}
                              </>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant={trigger.isActive ? 'success' : 'secondary'} size="sm">
                              {trigger.isActive ? 'Active' : 'Paused'}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-sm text-neutral-600">
                            <div>{formatRelativeDate(trigger.lastFiredAt)}</div>
                            {trigger.fireCount > 0 && (
                              <div className="text-xs text-neutral-400">
                                {trigger.fireCount} {trigger.fireCount === 1 ? 'time' : 'times'}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => openEdit(trigger)}
                                className="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded shadow-sm hover:bg-primary-700 transition-colors"
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => setDeletingId(trigger.id)}
                                className="px-3 py-1.5 text-xs font-medium text-danger-600 border border-danger-300 rounded shadow-sm hover:bg-danger-50 transition-colors"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Card>
              )}
            </>
          ) : (
            <EventsTab
              eventDefinitions={eventDefinitions}
              workflows={workflows}
              tags={tags}
              onTagsChanged={loadTags}
              onSubmit={handleSubmitEventDefinition}
              onDelete={handleDeleteEventDefinition}
            />
          )}
        </div>
      </div>

      <TriggerFormModal
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSubmit={handleSubmitTrigger}
        editing={editing}
        eventDefinitions={eventDefinitions}
        workflows={workflows}
        teamMembers={teamMembers}
      />

      <ConfirmationModal
        isOpen={!!deletingId}
        onClose={() => setDeletingId(null)}
        onAction={handleDeleteTrigger}
        title="Delete Trigger"
        description="This will remove the trigger configuration. Workflows already created by it will not be affected."
        actionText="Delete"
      />
    </div>
  );
};

export default Triggers;
